<!--
    The calendar's toolbar (PlanupCalendar.vue, every view): previous / next, the period shown (a
    click opens a month picker), היום, the filter chips (what the calendar shows), the crew-members
    switch and the view switch (חודש / שבוע / יום). Presentation only: every change is emitted and
    the parent owns the state. Wraps onto a second line when the calendar is narrow; on a phone the
    chips scroll sideways in one line.
-->
<template>
    <div class="pcal-toolbar">

        <div class="pcal-tb-nav"
             v-if="showNav">

            <button type="button"
                    class="pcal-tb-btn pcal-tb-prev"
                    title="הקודם"
                    @click="$emit('prev')">
                <i class="fa-solid fa-chevron-right"></i>
            </button>

            <div class="pcal-tb-period"
                 :class="{ open: pickerOpen }"
                 title="בחירת חודש"
                 @click="togglePicker">
                <span class="pcal-tb-face">
                    <i class="fa-regular fa-calendar"></i>
                    <span class="pcal-tb-title">{{ title }}</span>
                </span>
                <!-- hidden: the box takes the width of the widest, so the arrows stay put -->
                <span v-for="sample in titleSamples"
                      :key="sample"
                      class="pcal-tb-sample"
                      aria-hidden="true">{{ sample }}</span>
            </div>

            <button type="button"
                    class="pcal-tb-btn pcal-tb-next"
                    title="הבא"
                    @click="$emit('next')">
                <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button type="button"
                    class="pcal-tb-today"
                    @click="$emit('today')">
                היום
            </button>

            <div class="pcal-monthpicker"
                 v-if="pickerOpen">
                <div class="pcal-mp-year">
                    <button type="button"
                            title="שנה קודמת"
                            @click="pickerYear--">
                        <i class="fa-solid fa-chevron-right"></i>
                    </button>
                    <span>{{ pickerYear }}</span>
                    <button type="button"
                            title="שנה הבאה"
                            @click="pickerYear++">
                        <i class="fa-solid fa-chevron-left"></i>
                    </button>
                </div>
                <div class="pcal-mp-grid">
                    <div v-for="(name, index) in monthNames"
                         :key="index"
                         :class="{ selected: isShownMonth(index), current: isCurrentMonth(index) }"
                         @click="pickMonth(index)">
                        {{ name }}
                    </div>
                </div>
            </div>

        </div>

        <div class="pcal-tb-chips"
             v-if="chips && chips.length">
            <label v-for="chip in chips"
                   :key="chip.key"
                   class="pcal-chip"
                   :class="[chip.cls, { checked: chip.checked }]"
                   :data-chip="chip.key"
                   @click.prevent="$emit('toggle-chip', chip.key)">
                <span class="pcal-chip-box"><i class="fa-solid fa-check"></i></span>
                <span>{{ chip.label }}</span>
            </label>
        </div>

        <div class="pcal-tb-end">

            <label class="pcal-switch"
                   v-if="showCrewToggle">
                <input type="checkbox"
                       :checked="showCrewMembers"
                       @change="$emit('toggle-crew')">
                <span class="pcal-switch-track"></span>
                <span class="pcal-switch-label">אנשי צוות</span>
            </label>

            <div class="pcal-viewselect"
                 v-if="!hideDisplayTypeSelector">
                <button type="button"
                        class="pcal-viewselect-btn"
                        @click="toggleViewMenu">
                    <span>{{ displayType ? displayType.name : '' }}</span>
                    <i class="fa-solid fa-chevron-down"></i>
                </button>
                <div class="pcal-viewselect-menu"
                     v-if="viewMenuOpen">
                    <div v-for="type in displayTypes"
                         :key="type.value"
                         :class="{ active: displayType && displayType.value == type.value }"
                         @click="chooseDisplay(type)">
                        {{ type.name }}
                    </div>
                </div>
            </div>

        </div>

    </div>
</template>
<script>
export default {
    name: 'CalendarToolbar',
    props: {
        title: { default: '' },
        // every title the view can show, one per shape (PlanupCalendar's titleShapes)
        titleSamples: { default: () => [] },
        showNav: { default: true },
        // the date the calendar shows - the month picker opens on its year
        anchorDate: { default: null },
        // [{ key, label, cls, checked }] - cls carries the color classes (calendar-colors.js)
        chips: { default: () => [] },
        showCrewToggle: { default: true },
        showCrewMembers: { default: false },
        hideDisplayTypeSelector: { default: false },
        // [{ name, value }]
        displayTypes: { default: () => [] },
        displayType: { default: null },
    },
    data() {
        const monthFormat = new Intl.DateTimeFormat('he', { month: 'long' });
        const monthNames = [];
        for (let i = 0; i < 12; i++)
            monthNames.push(monthFormat.format(new Date(2026, i, 1)));

        return {
            pickerOpen: false,
            pickerYear: new Date().getFullYear(),
            viewMenuOpen: false,
            monthNames,
        };
    },
    methods: {

        anchor() {
            const date = this.anchorDate ? new Date(this.anchorDate) : new Date();
            return isNaN(date.getTime()) ? new Date() : date;
        },

        togglePicker() {
            if (this.pickerOpen) {
                this.closeMenus();
                return;
            }
            this.pickerYear = this.anchor().getFullYear();
            this.viewMenuOpen = false;
            this.pickerOpen = true;
        },

        toggleViewMenu() {
            const open = !this.viewMenuOpen;
            this.closeMenus();
            this.viewMenuOpen = open;
        },

        isShownMonth(index) {
            const date = this.anchor();
            return date.getFullYear() == this.pickerYear && date.getMonth() == index;
        },

        isCurrentMonth(index) {
            const today = new Date();
            return today.getFullYear() == this.pickerYear && today.getMonth() == index;
        },

        pickMonth(index) {
            this.$emit('pick', new Date(this.pickerYear, index, 1));
            this.closeMenus();
        },

        chooseDisplay(type) {
            this.$emit('change-display', type);
            this.closeMenus();
        },

        closeMenus() {
            this.pickerOpen = false;
            this.viewMenuOpen = false;
        },

        // a click anywhere else closes the open menu
        onDocumentClick(event) {
            if ((this.pickerOpen || this.viewMenuOpen) && this.$el && !this.$el.contains(event.target))
                this.closeMenus();
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick, true);
    },
}
</script>
<style>
.pcal-toolbar {
    direction: rtl;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
    padding: 6px 0 12px;
    position: relative;
    user-select: none;
}

/* two rows: the navigation with the switches at the far end, then the chips */
.pcal-tb-nav {
    order: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    position: relative;
    min-width: 0;
    max-width: 100%;
}

.pcal-tb-btn,
.pcal-tb-today,
.pcal-tb-period,
.pcal-viewselect-btn {
    height: 38px;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    background: #fff;
    color: #475569;
    cursor: pointer;
    transition: background-color .12s, border-color .12s, color .12s;
    font-family: inherit;
}

.pcal-tb-btn,
.pcal-tb-today {
    flex-shrink: 0;
}

.pcal-tb-btn {
    width: 38px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
}

.pcal-tb-btn:hover,
.pcal-tb-today:hover,
.pcal-viewselect-btn:hover {
    background: #f1f5f9;
    color: #1e293b;
}

/* one grid cell holds the title and, hidden, the widest titles the view can show: the box keeps
   one width from period to period and the title sits in its middle. A row too narrow for them
   (a week on a phone) shrinks the box and the title ends in … - the buttons stay on screen */
.pcal-tb-period {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    min-width: 0;
    overflow: hidden;
    padding: 0 14px;
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.pcal-tb-face,
.pcal-tb-sample {
    grid-area: 1 / 1;
}

.pcal-tb-face {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    min-width: 0;
}

.pcal-tb-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* the room of the icon + the gap */
.pcal-tb-sample {
    visibility: hidden;
    padding-right: 24px;
}

.pcal-tb-period:hover,
.pcal-tb-period.open {
    border-color: #c7bdf0;
    background: #faf8ff;
}

.pcal-tb-period i {
    flex-shrink: 0;
    width: 15px;
    text-align: center;
    color: #6d5bd0;
    font-size: 15px;
}

.pcal-tb-today {
    padding: 0 12px;
    font-size: 13px;
}

/* month picker */
.pcal-monthpicker {
    position: absolute;
    top: 44px;
    right: 44px;
    z-index: 40;
    width: 270px;
    padding: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, .14);
}

.pcal-mp-year {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
}

.pcal-mp-year button {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 7px;
    background: none;
    color: #475569;
    cursor: pointer;
}

.pcal-mp-year button:hover {
    background: #f1f5f9;
}

.pcal-mp-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.pcal-mp-grid div {
    padding: 8px 4px;
    border-radius: 8px;
    text-align: center;
    font-size: 13px;
    color: #334155;
    cursor: pointer;
}

.pcal-mp-grid div:hover {
    background: #f1f5f9;
}

.pcal-mp-grid div.current {
    box-shadow: inset 0 0 0 1px #8cb8f5;
}

.pcal-mp-grid div.selected {
    background: #6d5bd0;
    color: #fff;
}

/* filter chips */
.pcal-tb-chips {
    order: 3;
    flex-basis: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.pcal-chip {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    margin: 0;
    padding: 0 11px 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    background: #fff;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color .12s, border-color .12s, color .12s;
}

.pcal-chip:hover {
    background: #f8fafc;
}

.pcal-chip.checked {
    background: rgba(var(--pc-rgb), .13);
    border-color: rgba(var(--pc-rgb), .3);
    color: var(--pc-ink);
}

.pcal-chip.checked:hover {
    background: rgba(var(--pc-rgb), .2);
}

.pcal-chip-box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid #cbd5e1;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    color: #fff;
    flex: 0 0 16px;
}

.pcal-chip-box i {
    opacity: 0;
}

.pcal-chip.checked .pcal-chip-box {
    background: rgb(var(--pc-rgb));
    border-color: rgb(var(--pc-rgb));
}

.pcal-chip.checked .pcal-chip-box i {
    opacity: 1;
}

/* the left end of the first row: crew switch + view switch */
.pcal-tb-end {
    order: 2;
    display: flex;
    align-items: center;
    gap: 14px;
    margin-right: auto;
}

.pcal-switch {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: #475569;
}

.pcal-switch input {
    display: none;
}

.pcal-switch-track {
    width: 36px;
    height: 20px;
    border-radius: 10px;
    background: #e2e8f0;
    position: relative;
    transition: background-color .15s;
    flex: 0 0 36px;
}

.pcal-switch-track::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
    transition: transform .15s;
}

.pcal-switch input:checked+.pcal-switch-track {
    background: #6d5bd0;
}

.pcal-switch input:checked+.pcal-switch-track::after {
    transform: translateX(-16px);
}

.pcal-viewselect {
    position: relative;
}

.pcal-viewselect-btn {
    min-width: 98px;
    padding: 0 12px 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 14px;
    color: #1e293b;
}

.pcal-viewselect-btn i {
    font-size: 11px;
    color: #64748b;
}

.pcal-viewselect-menu {
    position: absolute;
    top: 42px;
    right: 0;
    z-index: 40;
    min-width: 120px;
    padding: 4px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, .12);
}

.pcal-viewselect-menu div {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #334155;
    cursor: pointer;
}

.pcal-viewselect-menu div:hover {
    background: #f1f5f9;
}

.pcal-viewselect-menu div.active {
    color: #6d5bd0;
    font-weight: 600;
}

@media (max-width: 767px) {

    .pcal-toolbar {
        gap: 8px;
        padding: 4px 0 8px;
    }

    .pcal-tb-btn,
    .pcal-tb-today,
    .pcal-tb-period,
    .pcal-viewselect-btn {
        height: 34px;
    }

    .pcal-tb-btn {
        width: 34px;
    }

    .pcal-tb-period {
        padding: 0 10px;
        font-size: 15px;
    }

    .pcal-monthpicker {
        right: 0;
        width: 250px;
    }

    /* the chips keep one line and scroll sideways */
    .pcal-tb-chips {
        flex-wrap: nowrap;
        width: 100%;
        overflow-x: auto;
        padding-bottom: 2px;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    .pcal-tb-chips::-webkit-scrollbar {
        display: none;
    }

    .pcal-chip {
        height: 30px;
        padding: 0 9px;
        font-size: 12px;
    }

    .pcal-tb-end {
        margin-right: 0;
        gap: 10px;
    }

    .pcal-viewselect-btn {
        min-width: 84px;
        font-size: 13px;
    }
}
</style>
